import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { Check, Loader2, Star, Leaf, Heart, Shield } from 'lucide-react';
import CommitmentSection from '../components/CommitmentSection';
import { optimizeImageUrl } from '../utils/imageOptimizer';

// New Arrivals Section - 2 products side by side on desktop
const NewArrivalsSection = ({ products, onProductClick }) => {
  if (!products || products.length === 0) return null;

  // Take up to 2 products for the grid
  const displayProducts = products.slice(0, 2);

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900">New Arrivals</h2>
          <div className="w-16 h-0.5 bg-[#2d4a47] mx-auto mt-4"></div>
        </div>
        
        {/* 2-column grid on desktop, single column on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {displayProducts.map((product) => (
            <NewArrivalCard
              key={product.id}
              product={product}
              onClick={() => onProductClick(product)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// New Arrival Card - larger format for 50/50 split
const NewArrivalCard = ({ product, onClick }) => {
  const defaultVariant = product.variants?.[0];
  const price = defaultVariant?.price || 0;
  const comparePrice = defaultVariant?.comparePrice || 0;

  return (
    <div 
      onClick={onClick}
      className="group cursor-pointer"
    >
      <div className="relative overflow-hidden bg-[#f5f1eb] rounded-lg aspect-square mb-6">
        <img
          src={optimizeImageUrl(product.images?.[0], { width: 600 })}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          width={600}
          height={600}
        />
        {product.isNew && (
          <span className="absolute top-4 left-4 bg-[#2d4a47] text-white text-xs px-4 py-2 tracking-wider">
            NEW
          </span>
        )}
        {product.onSale && comparePrice > price && (
          <span className="absolute top-4 right-4 bg-rose-600 text-white text-xs px-4 py-2 tracking-wider">
            SALE
          </span>
        )}
      </div>
      <div className="text-center">
        <div className="flex justify-center mb-3">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <h3 className="text-xl font-medium text-gray-900 mb-2 group-hover:text-[#2d4a47] transition-colors">
          {product.name}
        </h3>
        <p className="text-gray-500 mb-4 line-clamp-2 max-w-md mx-auto">
          {product.description}
        </p>
        <div className="flex justify-center items-center gap-3 mb-6">
          <span className="text-xl font-medium text-gray-900">${price.toFixed(2)}</span>
        </div>
        <button className="inline-block bg-[#2d4a47] text-white text-sm tracking-wider px-8 py-3 hover:bg-[#1e3633] transition-colors">
          SHOP NOW
        </button>
      </div>
    </div>
  );
};

// Featured Product Section (Full Width with Details)
const FeaturedProductSection = ({ product, featuredConfig, isReversed = false, bgColor = 'bg-[#f5f1eb]' }) => {
  if (!product) return null;

  const defaultVariant = product.variants?.[0];
  const price = defaultVariant?.price || 0;

  return (
    <section className={`py-16 md:py-24 ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
          {/* Image */}
          <div className={isReversed ? 'order-1 lg:order-2' : 'order-1'}>
            <div className="relative">
              <img
                src={optimizeImageUrl(product.images?.[0], { width: 600 })}
                alt={product.name}
                className="w-full max-w-lg mx-auto rounded-lg shadow-xl"
                width={600}
                height={600}
                loading="lazy"
              />
              {product.onSale && (
                <span className="absolute top-4 left-4 bg-rose-600 text-white text-sm px-4 py-2 tracking-wider">
                  SALE
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className={isReversed ? 'order-2 lg:order-1' : 'order-2'}>
            <div className="flex mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-2 text-sm text-gray-500">(45 reviews)</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 leading-tight mb-4">
              {featuredConfig?.title || product.name}
            </h2>
            
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              {featuredConfig?.description || product.description}
            </p>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-2xl font-medium text-gray-900">${price.toFixed(2)}</span>
              {defaultVariant?.comparePrice > price && (
                <span className="text-lg text-gray-400 line-through">${defaultVariant.comparePrice.toFixed(2)}</span>
              )}
            </div>

            <Link
              to={`/products/${product.slug}`}
              className="inline-block bg-[#2d4a47] text-white text-sm tracking-wider px-8 py-4 hover:bg-[#1e3633] transition-colors"
            >
              SHOP NOW
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Information Section Component
const InfoSection = ({ section, isDark = false }) => {
  const bgColor = isDark ? 'bg-[#2d4a47]' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const descColor = isDark ? 'text-gray-200' : 'text-gray-600';

  return (
    <section className={`py-16 md:py-24 ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
          <div className={section.imagePosition === 'right' ? 'order-1' : 'order-2 lg:order-1'}>
            <h2 className={`text-3xl md:text-4xl font-serif leading-tight mb-6 ${textColor}`}>
              {section.title}
            </h2>
            {section.description && (
              <p className={`leading-relaxed whitespace-pre-line ${descColor}`}>
                {section.description}
              </p>
            )}
            {section.commitments && (
              <ul className="space-y-4 mt-6">
                {section.commitments.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className={`mt-1 flex-shrink-0 ${isDark ? 'text-white' : 'text-[#2d4a47]'}`} size={18} />
                    <span className={descColor}>{item.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className={section.imagePosition === 'right' ? 'order-2' : 'order-1 lg:order-2'}>
            <img
              src={optimizeImageUrl(section.image, { width: 600 })}
              alt={section.title}
              className="w-full rounded-lg shadow-lg"
              width={600}
              height={600}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Carousel
const TestimonialsSection = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const reviews = testimonials?.reviews || [];

  useEffect(() => {
    if (reviews.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  if (!reviews.length) return null;

  return (
    <section className="py-16 md:py-24 bg-[#f3efe9]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 leading-tight">
            {testimonials.title}
          </h2>
          <div className="w-16 h-0.5 bg-[#2d4a47] mx-auto mt-4"></div>
        </div>

        {/* Featured Review */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            {[...Array(reviews[currentIndex]?.rating || 5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <p className="text-xl md:text-2xl text-gray-700 italic leading-relaxed mb-6 max-w-3xl mx-auto">
            "{reviews[currentIndex]?.text}"
          </p>
          <div>
            <span className="font-semibold text-gray-900 block text-lg">
              {reviews[currentIndex]?.author}
            </span>
            <span className="text-sm text-gray-500">
              {reviews[currentIndex]?.age} years old
            </span>
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-[#2d4a47]' : 'bg-gray-300'
              }`}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>

        {/* All Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {reviews.map((review, index) => (
            <div 
              key={review.id}
              onClick={() => setCurrentIndex(index)}
              className={`bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer ${
                index === currentIndex ? 'ring-2 ring-[#2d4a47]' : ''
              }`}
            >
              <div className="flex justify-center mb-3">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 italic text-center text-sm mb-3 line-clamp-3">
                "{review.text}"
              </p>
              <div className="text-center pt-3 border-t border-gray-100">
                <span className="font-semibold text-gray-900 block text-sm">{review.author}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust Badges Section
const TrustBadges = () => (
  <section className="py-12 bg-white border-y border-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div className="flex flex-col items-center">
          <Leaf className="w-8 h-8 text-[#2d4a47] mb-3" />
          <span className="text-sm font-medium text-gray-900">100% Natural</span>
          <span className="text-xs text-gray-500">Pure Botanicals</span>
        </div>
        <div className="flex flex-col items-center">
          <Heart className="w-8 h-8 text-[#2d4a47] mb-3" />
          <span className="text-sm font-medium text-gray-900">Cruelty Free</span>
          <span className="text-xs text-gray-500">No Animals Harmed</span>
        </div>
        <div className="flex flex-col items-center">
          <Shield className="w-8 h-8 text-[#2d4a47] mb-3" />
          <span className="text-sm font-medium text-gray-900">Clean Formula</span>
          <span className="text-xs text-gray-500">No Harsh Chemicals</span>
        </div>
        <div className="flex flex-col items-center">
          <svg className="w-8 h-8 text-[#2d4a47] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <span className="text-sm font-medium text-gray-900">Sustainable</span>
          <span className="text-xs text-gray-500">Eco-Friendly Packaging</span>
        </div>
      </div>
    </div>
  </section>
);

// Hero Section
const HeroSection = ({ hero, onShopClick }) => (
  <section className="relative flex items-center bg-[#faf8f5]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 md:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="order-2 lg:order-1 text-center lg:text-left">
          <span className="inline-block text-sm tracking-[0.2em] text-[#2d4a47] mb-4 font-medium">
            PURE. ORGANIC. NATURAL.
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-gray-900 leading-tight mb-6">
            {hero.title}
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
            {hero.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link
              to={hero.buttonLink}
              className="inline-block bg-[#2d4a47] text-white text-sm tracking-wider px-8 py-4 hover:bg-[#1e3633] transition-colors text-center"
            >
              {hero.buttonText}
            </Link>
            <button
              onClick={onShopClick}
              className="inline-block border-2 border-[#2d4a47] text-[#2d4a47] text-sm tracking-wider px-8 py-4 hover:bg-[#2d4a47] hover:text-white transition-colors text-center"
            >
              VIEW ALL PRODUCTS
            </button>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <img
            src={optimizeImageUrl(hero.image, { width: 600 })}
            alt="Natural skincare product"
            className="w-full max-w-lg mx-auto"
            width={600}
            height={600}
            fetchpriority="high"
          />
        </div>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const { siteData, loading } = useSite();
  const { hero, sections, testimonials, products, featuredProducts } = siteData;

  // Get featured products from configuration or default to first products
  const getFeaturedProduct = (index) => {
    const config = featuredProducts?.[index];
    if (config?.productId) {
      return {
        product: products.find(p => p.id === config.productId),
        config
      };
    }
    // Fallback to product by index if no configuration
    return {
      product: products[index],
      config: null
    };
  };

  const handleProductClick = (product) => {
    window.location.href = `/products/${product.slug}`;
  };

  const scrollToProducts = () => {
    window.location.href = '/products';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8f5]">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#2d4a47] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const featured1 = getFeaturedProduct(0);
  const featured2 = getFeaturedProduct(1);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection hero={hero} onShopClick={scrollToProducts} />

      {/* Trust Badges */}
      <TrustBadges />

      {/* New Arrivals - 2 products 50/50 split */}
      <NewArrivalsSection 
        products={products} 
        onProductClick={handleProductClick}
      />

      {/* Info Section 1 */}
      {sections[0] && <InfoSection section={sections[0]} />}

      {/* Featured Product 1 */}
      {featured1.product && (
        <FeaturedProductSection 
          product={featured1.product} 
          featuredConfig={featured1.config}
          bgColor="bg-[#f5f1eb]"
        />
      )}

      {/* Info Section 2 (dark) */}
      {sections[1] && <InfoSection section={sections[1]} isDark />}

      {/* Featured Product 2 */}
      {featured2.product && (
        <FeaturedProductSection 
          product={featured2.product} 
          featuredConfig={featured2.config}
          isReversed 
          bgColor="bg-white"
        />
      )}

      {/* Testimonials */}
      <TestimonialsSection testimonials={testimonials} />

      {/* Commitment Section */}
      <CommitmentSection />
    </div>
  );
};

export default HomePage;
